import React from 'react'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { Button } from 'primereact/button'
import { Menu } from 'primereact/menu'
import { Checkbox } from 'primereact/checkbox'
import { ShippingOrder } from '../DeShippingTableWrapper'
import { useOrderData } from '../hooks/useOrderData'
import { useOrderTabs } from '../hooks/useOrderTabs'
import { useLineItems } from '../hooks/useLineItems'
import { AdminControlDialog } from './AdminControlDialog'
import { ViewOrderInfoDialog } from './dialogs/ViewOrderInfoDialog'
import { AssignLocationsDialog } from './AssignLocationsDialog'
import { AcceptPendingReviewDialog } from './dialogs/lineItemReviewer'
import { RejectedItemsDialog } from './RejectedItemsDialog'
import { BulkOrderAssignmentDialog } from './dialogs/bulkOrderAssignment'
import { BulkRejectedItemsDialog } from './dialogs/bulkRejectedItems'
import { orderStatusTemplate } from './shared/OrderStatusTemplate'
import { OrderFulfillmentLocationTemplate } from './shared/OrderFulfillmentLocationTemplate'
import { dateTemplate } from './shared/DateTemplate'
import { OrderAssignmentStatusTemplate } from './shared/OrderAssignmentStatusTemplate'
import { Toolbar } from 'primereact/toolbar'
import { useOrderSelection } from '../hooks/useOrderSelection'
import { useLineItemSelection } from '../hooks/useLineItemSelection'

type OrderDataType = ReturnType<typeof useOrderData>

interface OrdersTableProps {
  orderData: OrderDataType
  orderSelection: ReturnType<typeof useOrderSelection>
  orderTabs: ReturnType<typeof useOrderTabs>
  lineItemsHook: ReturnType<typeof useLineItems>
  lineItemSelection: ReturnType<typeof useLineItemSelection>
  onPage: (event: any) => void
  onSort: (event: any) => void
  onFilter: (event: any) => void
}

// Add this new template function after the existing templates (around line 100-120)
const shopifyLinkTemplate = (rowData: ShippingOrder) => {
  const handleShopifyRedirect = () => {

    const shopifyUrl = `https://admin.shopify.com/store/designereyes/orders/${rowData.order_id}`
    window.open(shopifyUrl, '_blank', 'noopener,noreferrer')
  }

  return (
    <Button
      icon='fa fa-eye'
      className='p-button-text p-button-plain'
      onClick={handleShopifyRedirect}
      tooltip='View Order in Shopify'
    />
  )
}

export const OrdersTable: React.FC<OrdersTableProps> = ({
  orderSelection,
  orderData,
  orderTabs,
  lineItemsHook,
  lineItemSelection,
  onPage,
  onSort,
  onFilter,
}) => {
  // Move refs to component level
  const menuRefs = React.useRef<{ [key: string]: Menu | null }>({});

  // Dialog state management
  const [dialogState, setDialogState] = React.useState({
    adminControl: false,
    viewOrderInfo: false,
    assignLocations: false,
    acceptPendingReview: false,
    rejectedItems: false,
    bulkAssignment: false,
    bulkRejectedItems: false,
  });

  const openDialog = (dialogType: keyof typeof dialogState, order: ShippingOrder) => {
    orderSelection.setSelectedOrder(order);
    
    setDialogState(prev => ({
      ...prev,
      [dialogType]: true,
      selectedOrder: order
    }));
  };

  const closeDialog = (dialogType: keyof typeof dialogState) => {
    setDialogState(prev => ({
      ...prev,
      [dialogType]: false,
      selectedOrder: null
    }));
    // Note: We're keeping the selected order in orderData.selectedOrder 
    // so it persists until a new order is selected
  };

  // Status click handler - maps status types to appropriate dialogs
  const handleStatusClick = (order: ShippingOrder, statusType: string) => {
    console.log('handleStatusClick', order, statusType);
    switch (statusType) {
      case 'Needs Assignment':
        openDialog('assignLocations', order);
        break;
      case 'Awaiting Inspection':
        openDialog('acceptPendingReview', order);
        break;
      case 'Items Declined':
        openDialog('rejectedItems', order);
        break;
      case 'In Progress':
      case 'No Action Needed':
        openDialog('viewOrderInfo', order);
        break;
      default:
        openDialog('viewOrderInfo', order);
    }
  };

  React.useEffect(() => {
  }, [orderData.orders]);

  // Filter orders based on active tab using the hook
  const filteredOrders = React.useMemo(() => {
    return orderTabs.getFilteredOrders(orderData.orders);
  }, [orderData.orders, orderTabs]);

  // Check if bulk selection is enabled for current tab
  const isBulkSelectionEnabled = ['needs_assignment', 'declined'].includes(orderTabs.activeTab);

  // Handle bulk selection - only select orders from current tab
  const handleSelectAll = () => {
    const allSelected = filteredOrders.every(order => orderSelection.isOrderSelected(order));
    
    if (allSelected) {
      orderSelection.clearSelection();
    } else {
      // Select all filtered orders from current tab only
      filteredOrders.forEach(order => {
        if (!orderSelection.isOrderSelected(order)) {
          orderSelection.selectOrder(order);
        }
      });
    }
  };

  // Selection column template
  const selectionTemplate = (rowData: ShippingOrder) => {
    const isSelected = orderSelection.isOrderSelected(rowData);
    
    const handleToggle = (e: any) => {
      e.preventDefault();
      e.stopPropagation();
      console.log('Toggle selection for order:', rowData.order_id, 'currently selected:', isSelected);
      orderSelection.toggleOrderSelection(rowData);
    };
    
    return (
      <Checkbox
        checked={isSelected}
        onChange={handleToggle}
        inputId={`checkbox-${rowData.order_id}`}
      />
    );
  };

  // Header selection template
  const headerSelectionTemplate = () => {
    const selectedOrdersInView = filteredOrders.filter(order => orderSelection.isOrderSelected(order));
    const allSelected = filteredOrders.length > 0 && selectedOrdersInView.length === filteredOrders.length;
    const someSelected = selectedOrdersInView.length > 0;
    
    return (
      <Checkbox
        checked={allSelected}
        onChange={handleSelectAll}
        className={someSelected && !allSelected ? 'p-checkbox-indeterminate' : ''}
      />
    );
  };

  // Tab navigation component with bulk actions
  const renderTabNavigation = () => {
    const selectedCount = orderSelection.getSelectedOrdersCount();
    const showBulkActions = isBulkSelectionEnabled && selectedCount > 0;

    const leftContent = (
      <div className="flex gap-2" data-testid="order-tabs-container">
        {orderTabs.tabConfig.map((tab) => (
          <Button
            key={tab.key}
            label={tab.label}
            data-testid={`tab-${tab.key}`}
            className={`p-button-sm ${
              orderTabs.activeTab === tab.key 
                ? 'p-button-primary' 
                : 'p-button-outlined p-button-secondary'
            }`}
            onClick={() => {
              orderTabs.setActiveTab(tab.key);
              // Clear selection when switching tabs
                orderSelection.clearSelection();
            }}
            style={{ 
              fontSize: '0.875rem',
              padding: '0.75rem 1.25rem',
              minHeight: '40px',
              fontWeight: '500'
            }}
          />
        ))}
      </div>
    );

    const rightContent = showBulkActions ? (
      <div 
        className="flex align-items-center justify-content-between gap-3 px-3 py-2 border-1 border-primary-200 bg-primary-50 border-round" 
        style={{ minHeight: '40px' }}
        data-testid="bulk-actions-toolbar"
      >
        {/* Selection info on left */}
        <div className="flex align-items-center gap-2">
          <i className="pi pi-check-circle text-primary" style={{ fontSize: '1rem' }}></i>
          <span 
            className="text-primary font-semibold text-sm" 
            style={{ lineHeight: '1' }}
            data-testid="selection-count"
          >
            {selectedCount} selected
          </span>
        </div>
        
        {/* Bulk Action Button - shows different buttons based on active tab */}
        {orderTabs.activeTab === 'needs_assignment' && (
          <Button
            label="Bulk Assign"
            icon="pi pi-map-marker"
            className="p-button-sm p-button-primary"
            data-testid="bulk-assign-button"
            style={{ 
              fontSize: '0.875rem', 
              padding: '0.4rem 1rem',
              height: '32px'
            }}
            onClick={() => setDialogState(prev => ({ ...prev, bulkAssignment: true }))}
          />
        )}
        {orderTabs.activeTab === 'declined' && (
          <Button
            label="Bulk Handle Rejected"
            icon="pi pi-exclamation-triangle"
            className="p-button-sm p-button-primary"
            data-testid="bulk-rejected-button"
            style={{ 
              fontSize: '0.875rem', 
              padding: '0.4rem 1rem',
              height: '32px'
            }}
            onClick={() => setDialogState(prev => ({ ...prev, bulkRejectedItems: true }))}
          />
        )}
        
        {/* Clear on right */}
        <Button
          label="Clear"
          className="p-button-text p-button-primary p-button-sm"
          data-testid="clear-selection-button"
          style={{ 
            fontSize: '0.875rem', 
            padding: '0.4rem 0.8rem',
            height: '32px'
          }}
          onClick={orderSelection.clearSelection}
        />
      </div>
    ) : null;

    return (
      <div className="mb-4">
        <Toolbar 
          left={leftContent} 
          right={rightContent}
          className="border-none bg-transparent p-0"
          style={{ minHeight: 'auto' }}
        />
      </div>
    );
  };

  const pendingReviewItemsTemplate = (rowData: ShippingOrder, onClick?: (order: ShippingOrder) => void) => {
    const pendingItems = rowData.pending_review_items || 0;
    
    const handleClick = () => {
      if (onClick) {
        onClick(rowData);
      }
    };
    
    return (
      <span 
        className="fw-semibold text-warning"
        onClick={handleClick}
        style={onClick ? { cursor: 'pointer' } : undefined}
        data-testid={`items-pending-review-${rowData.order_id}`}
      >
        {pendingItems} {pendingItems === 1 ? 'Item' : 'Items'} Pending Review
      </span>
    );
  };

  // Declined items template
  const declinedItemsTemplate = (rowData: ShippingOrder) => {
    const declinedItems = rowData.declined_items || 0;
    
    const handleClick = () => {
      openDialog('rejectedItems', rowData);
    };
    
    return (
      <span 
        className="fw-semibold text-danger"
        onClick={handleClick}
        style={{ cursor: 'pointer' }}
        data-testid={`items-declined-${rowData.order_id}`}
      >
        {declinedItems} {declinedItems === 1 ? 'Item' : 'Items'} Declined
      </span>
    );
  };

  // Order name template - simple display
  const orderNameTemplate = (rowData: ShippingOrder) => {
    return (
      <span className="fw-semibold">{rowData.order_name}</span>
    );
  };

  // Items template - displays total item count
  const itemsTemplate = (rowData: ShippingOrder) => {
    return (
      <span className="fw-semibold">
        {rowData.total_items} {rowData.total_items === 1 ? 'item' : 'items'}
      </span>
    );
  };

  // Order total template - formats currency
  const orderTotalTemplate = (rowData: ShippingOrder) => {
    return (
      <span className="fw-semibold">
        ${rowData.order_subtotal.toFixed(2)}
      </span>
    );
  };


  // Actions dropdown template
  const actionsTemplate = (rowData: ShippingOrder) => {
    const hasUnassignedItems = rowData.unassigned_items > 0;
    const hasPendingReviewItems = (rowData.pending_review_items || 0) > 0;
    const hasDeclinedItems = (rowData.declined_items || 0) > 0;

    const menuItems = [
      {
        label: 'View Order Info',
        icon: 'pi pi-info-circle',
        command: () => openDialog('viewOrderInfo', rowData)
      },
      {
        label: 'Admin Control',
        icon: 'pi pi-cog',
        command: () => openDialog('adminControl', rowData)
      },
      ...(hasUnassignedItems ? [{
        label: 'Assign Locations',
        icon: 'pi pi-map-marker',
        command: () => openDialog('assignLocations', rowData),
        className: 'assign-locations-menu-item'
      }] : []),
      ...(hasPendingReviewItems ? [{
        label: 'Review Items',
        icon: 'pi pi-eye',
        command: () => openDialog('acceptPendingReview', rowData),
        className: 'review-items-menu-item'
      }] : []),
      ...(hasDeclinedItems ? [{
        label: 'Handle Rejected Items',
        icon: 'pi pi-exclamation-triangle',
        command: () => openDialog('rejectedItems', rowData),
        className: 'handle-rejected-items-menu-item'
      }] : [])
    ];

    return (
      <div>
        <Button
          label="More"
          icon="pi pi-ellipsis-h"
          iconPos="left"
          className="p-button-outlined p-button-sm"
          data-testid={`more-actions-btn-${rowData.order_id}`}
          onClick={(event) => menuRefs.current[rowData.order_id]?.toggle(event)}
          aria-label="More actions"
          style={{ 
            fontSize: '0.875rem',
            padding: '0.25rem 0.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.25rem'
          }}
        >
          <i className="pi pi-angle-down" style={{ marginLeft: '0.25rem', fontSize: '0.75rem' }}></i>
        </Button>
        <Menu
          ref={(el) => menuRefs.current[rowData.order_id] = el}
          model={menuItems}
          popup
          className="custom-actions-menu"
          data-testid={`more-actions-menu-${rowData.order_id}`}
        />
      </div>
    );
  };

  return (
    <div data-testid="orders-table-container">
      {renderTabNavigation()}
      
      <DataTable
        scrollable
        scrollHeight='700px'
        value={filteredOrders}
        loading={orderData.loading}
        paginator
        rows={orderData.pagination.rows}
        first={orderData.pagination.first}
        totalRecords={filteredOrders.length}
        onPage={onPage}
        onSort={onSort}
        sortField={orderData.sort.field}
        sortOrder={orderData.sort.order}
        responsiveLayout='scroll'
        className='mt-4'
        style={{background: 'transparent'}}
        filterDisplay='menu'
        filters={orderData.filters}
        onFilter={onFilter}
        paginatorTemplate="FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
        paginatorRight={null}
        data-testid="orders-table"
      >
        {/* Selection column - only visible in needs_assignment tab */}
        {isBulkSelectionEnabled && (
          <Column 
            header={headerSelectionTemplate} 
            body={selectionTemplate} 
            style={{width: '50px'}} 
            exportable={false}
          />
        )}

        {/* New Shopify link column - first position */}
        <Column 
          header='' 
          body={shopifyLinkTemplate} 
          style={{width: '50px'}} 
        />
        
        <Column 
          field="created_at" 
          header="Order Creation Date" 
          body={dateTemplate}
          sortable 
          filter 
          style={{width: '150px'}} 
        />
        <Column 
          field="fulfillment_location" 
          header="Fulfillment Location" 
          body={OrderFulfillmentLocationTemplate}
          style={{width: '150px'}} 
        />
        <Column 
          field="order_name" 
          header="Order Name" 
          body={orderNameTemplate}
          sortable 
          filter 
          style={{width: '200px'}} 
        />
        <Column 
          field="total_items" 
          header="Items" 
          body={itemsTemplate}
          sortable 
          filter
          style={{width: '100px'}} 
        />
        <Column 
          field="order_subtotal" 
          header="Order Total" 
          body={orderTotalTemplate}
          sortable 
          filter
          style={{width: '120px'}} 
        />
        {/* Order Status Column - Only shows in 'All' tab with click handler */}
        {orderTabs.activeTab === 'all' && (
          <Column 
            header="Order Status" 
            body={(rowData) => orderStatusTemplate(rowData, handleStatusClick)}
            style={{width: '150px'}} 
          />
        )}
        {orderTabs.activeTab === 'pending_review' && (
          <Column 
            field="pending_review_items" 
            header="Items Pending Review" 
            body={(rowData) => pendingReviewItemsTemplate(rowData, (order) => openDialog('acceptPendingReview', order))}
            sortable 
            filter
            style={{width: '150px'}} 
          />
        )}
        {orderTabs.activeTab === 'declined' && (
          <Column 
            field="declined_items" 
            header="Items Declined" 
            body={declinedItemsTemplate}
            sortable 
            filter
            style={{width: '130px'}} 
          />
        )}
        {orderTabs.activeTab === 'needs_assignment' && (
          <Column 
            header="Assignment Status" 
            body={(rowData) => OrderAssignmentStatusTemplate(rowData, (order) => openDialog('assignLocations', order))}
            style={{width: '200px'}} 
          />
        )}
        <Column 
          header="Actions" 
          body={actionsTemplate}
          style={{width: '80px'}} 
        />
      </DataTable>

      {/* Dialog Components */}
      <AdminControlDialog
        orderData={orderData}
        orderSelection={orderSelection}
        lineItemsHook={lineItemsHook}
        lineItemSelection={lineItemSelection}
        visible={dialogState.adminControl}
        onHide={() => closeDialog('adminControl')}
      />

      <ViewOrderInfoDialog
        orderSelection={orderSelection}
        lineItemsHook={lineItemsHook}
        lineItemSelection={lineItemSelection}
        visible={dialogState.viewOrderInfo}
        onHide={() => closeDialog('viewOrderInfo')}
      />

      <AssignLocationsDialog
        orderData={orderData}
        orderSelection={orderSelection}
        lineItemsHook={lineItemsHook}
        lineItemSelection={lineItemSelection}
        visible={dialogState.assignLocations}
        onHide={() => closeDialog('assignLocations')}
      />

      <AcceptPendingReviewDialog
        orderData={orderData}
        orderSelection={orderSelection}
        lineItemsHook={lineItemsHook} 
        lineItemSelection={lineItemSelection}
        visible={dialogState.acceptPendingReview}
        onHide={() => closeDialog('acceptPendingReview')}
      />

      <RejectedItemsDialog
        orderData={orderData}
        orderSelection={orderSelection}
        lineItemsHook={lineItemsHook}
        lineItemSelection={lineItemSelection}
        visible={dialogState.rejectedItems}
        onHide={() => closeDialog('rejectedItems')}
      />

      <BulkOrderAssignmentDialog
        orderSelection={orderSelection}
        visible={dialogState.bulkAssignment}
        onCloseDialog={() => closeDialog('bulkAssignment')}
        orderData={orderData}
        lineItemsHook={lineItemsHook}
        lineItemSelection={lineItemSelection}
      />

      <BulkRejectedItemsDialog
        orderSelection={orderSelection}
        visible={dialogState.bulkRejectedItems}
        onCloseDialog={() => closeDialog('bulkRejectedItems')}
        orderData={orderData}
        lineItemsHook={lineItemsHook}
        lineItemSelection={lineItemSelection}
      />
    </div>
  )
}